import { useQuery } from "@tanstack/react-query";
import ContactsService from "../../../../services/web/contacts-service";
import { useEffect, useMemo, useState } from "react";
import { FaEnvelope, FaPhoneAlt, FaWhatsapp } from "react-icons/fa";
import { getAssetPath } from "../../../../utils/get-asset-path";
import { Template } from "../../../../components/template/template";
import ProjectUtils from "../../../../utils/project-utils.js";

function ContactCard({ title, value, Icon, onCopy, copied }) {
    if (!value) return null;
    return (
        <button 
            type="button" 
            onClick={onCopy}
            className="
                relative flex items-center gap-4
                w-104 min-w-104 min-h-27
                rounded-lg bg-white shadow-sm
                cursor-pointer
                hover:bg-sky-700 hover:text-white hover:shadow-md
                transition
            "
        >
            <div className="flex w-22 shrink-0 justify-end">
                <Icon className="text-5xl" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col items-start">
                <p className="text-base mb-1">{title}</p>
                <span className="text-xl font-bold">{value}</span>
            </div>
            {copied && (
                <span className="absolute -top-3 -left-6 rounded bg-yellow-400 px-2 py-1 text-xs font-bold text-neutral-800 shadow">
                    Copiado!
                </span>
            )}
        </button>
    );
};

function LinkCard({ title, url, Icon }) {
    return (
        <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="
                relative flex items-center gap-4
                w-104 min-w-104 min-h-27
                rounded-lg bg-white shadow-sm
                text-[#676a6c]! no-underline
                hover:bg-sky-700! hover:text-white! hover:shadow-md
                transition
            "
        >
            <div className="flex w-22 shrink-0 justify-end">
                <Icon className="text-6xl" />
            </div>
            <div className="flex min-w-0 flex-1 flex-col items-start">
                <p className="text-xl mb-1 font-bold">{title}</p>
            </div> 
        </a>
    );
};

export default function Contacts() {
    const [copiedId, setCopiedId] = useState(null);

    const { data, isLoading } = useQuery({
        queryKey: ['contacts'],
        queryFn: ContactsService.contacts,
    });

    async function copy(text, id) {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const element = document.createElement("textarea");
            element.value = text;
            document.body.appendChild(element);
            element.select();
            document.execCommand("copy");
            document.body.removeChild(element);
        }

        setCopiedId(id);
    };

    useEffect(() => {
        if (!copiedId) return;
        const t = setTimeout(() => setCopiedId(null), 2500);
        return () => clearTimeout(t);
    }, [copiedId])

    const whats = "https://wa.me/551131835560";
    const phone = "(11) 3183-5560";
    const email = "suporte@multiway.com.br";

    const entityName = data?.entityName ?? "";
    const contacts = data?.contacts ?? [];
    const hasCompanyContacts = Array.isArray(contacts) && contacts.length > 0;

    const companyCards = useMemo(() => {
        if (!hasCompanyContacts) return [];
        const cards = [];

        contacts.forEach((c, idx) => {
            if (c?.phone) {
                cards.push({
                    key: `phone=${idx}`,
                    title: "Telefone",
                    value: ProjectUtils.formatPhone(c.phone),
                    Icon: FaPhoneAlt,
                    copyId: `company-phone-${idx}`,
                });
            }
            if (c?.email) {
                cards.push({
                    key: `email-${idx}`,
                    title: "Email",
                    value: c.email,
                    Icon: FaEnvelope,
                    copyId: `company-email-${idx}`,
                });
            }
        });

        return cards;
    }, [contacts, hasCompanyContacts]);

    const SuportColumn = (
        <div className="flex flex-col items-center">
            <h1 className="text-[#ffca28] font-bold mb-6! text-center">Suporte - Sentry</h1>
            <div className="flex flex-col gap-10">
                <LinkCard 
                    title="Whatsapp"
                    url={whats}
                    Icon={FaWhatsapp}
                />
                <ContactCard 
                    title="Telefone"
                    value={phone}
                    Icon={FaPhoneAlt}
                    onCopy={() => copy(phone, "phone-copy")}
                    copied={copiedId === "phone-copy"}
                />
                <ContactCard 
                    title="Email"
                    value={email}
                    Icon={FaEnvelope}
                    onCopy={() => copy(email, "email-copy")}
                    copied={copiedId === "email-copy"}
                />
            </div>
        </div>
    );

    const CompanyColumn = (
        <div className="flex flex-col items-center pt-12">
            <h1 className="text-[#ffca28] font-bold mb-6! text-center">{entityName}</h1>
            <div className="flex flex-col gap-10">
                {companyCards.map((c) => (
                    <ContactCard 
                        key={c.key}
                        title={c.title}
                        value={c.value}
                        Icon={c.Icon}
                        onCopy={() => copy(c.value, c.copyId)}
                        copied={copiedId === c.copyId}
                    />
                ))}
            </div>
        </div>
    );

    return (
        <Template>
            <div className="min-h-screen bg-[#031b34] flex flex-col items-center justify-between p-4">
                <div className="flex-1 flex items-center justify-center">
                    {!hasCompanyContacts ? (
                        <div className="w-full flex justify-center">
                            {SuportColumn}
                        </div>
                    ) : (
                        <div className="w-full max-w-6xl flex items-start justify-center gap-14">
                            <div className="flex-1 flex justify-center pr-10">{CompanyColumn}</div>
                            <div className="w-px self-stretch bg-white/20" />
                            <div className="flex-1 flex justify-center pl-10">{SuportColumn}</div>
                        </div>
                    )}
                </div>
                <footer className="flex flex-col items-center gap-4 text-center text-white">
                <a href="https://www.sentry.com.br" target="_blank" rel="noreferrer">
                    <img 
                    src={getAssetPath("img/logo.svg")}
                        alt="Sentry"
                        title="Sentry"
                        className="h-13"
                    />
                </a>
                <div className="text-xs">
                    &copy;  2004-{new Date().getFullYear()} Multiway Comércio e Representações Ltda. <br/>
                    Todos os direitos reservados
                </div>
            </footer>
            </div>   
        </Template>
    )
}